import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispositivos } from "../../hooks/useDispositivos";
import { DispositivoInterface } from "../../interfaces/DispositivoInterface";
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Smartphone, 
  CheckCircle, 
  AlertCircle, 
  Users,
  Filter
} from "lucide-react";

const Dispositivos: React.FC = () => {
  const navigate = useNavigate();
  const { dispositivos, isLoading, deleteDispositivo, isDeleting } = useDispositivos();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dispositivoToDelete, setDispositivoToDelete] = useState<number | null>(null);

  // Función para mapear estatus_id a estado legible
  const getEstadoFromId = (estatus_id: number): string => {
    switch (estatus_id) {
      case 1: return 'disponible';
      case 2: return 'asignado';
      case 3: return 'mantenimiento';
      case 4: return 'dañado';
      default: return 'unknown';
    }
  };

  // Función para obtener el icono según el estatus_id
  const getEstadoIcon = (estatus_id: number) => {
    const estado = getEstadoFromId(estatus_id);
    switch (estado) {
      case 'disponible':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'asignado':
        return <Users className="w-5 h-5 text-blue-500" />;
      case 'mantenimiento':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'dañado':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Smartphone className="w-5 h-5 text-gray-500" />;
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'disponible':
        return 'border-green-200 bg-green-50 text-green-800';
      case 'asignado':
        return 'border-blue-200 bg-blue-50 text-blue-800';
      case 'mantenimiento':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800';
      case 'dañado':
        return 'border-red-200 bg-red-50 text-red-800';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800';
    }
  };

  // Filtrar dispositivos
  const filteredDispositivos = useMemo(() => {
    // Asegurar que dispositivos sea un array
    const dispositivosArray = Array.isArray(dispositivos) ? dispositivos : [];
    
    return dispositivosArray.filter((dispositivo: DispositivoInterface) => {
      // Función helper para convertir a string de forma segura
      const safeString = (value: any): string => {
        return value ? String(value).toLowerCase() : '';
      };
      
      const matchesSearch = 
        safeString(dispositivo.nombre_dispositivo).includes(searchTerm.toLowerCase()) ||
        safeString(dispositivo.modelo).includes(searchTerm.toLowerCase()) ||
        safeString(dispositivo.numero_serie).includes(searchTerm.toLowerCase()) ||
        safeString(dispositivo.imei).includes(searchTerm.toLowerCase());
      
      const estadoDispositivo = getEstadoFromId(dispositivo.estatus_id);
      const matchesFilter = filterEstado === "todos" || estadoDispositivo === filterEstado;
      
      return matchesSearch && matchesFilter;
    });
  }, [dispositivos, searchTerm, filterEstado]);

  // Estadísticas
  const estadisticas = useMemo(() => {
    // Asegurar que dispositivos sea un array
    const dispositivosArray = Array.isArray(dispositivos) ? dispositivos : [];
    
    const total = dispositivosArray.length;
    const disponibles = dispositivosArray.filter(d => getEstadoFromId(d.estatus_id) === 'disponible').length;
    const asignados = dispositivosArray.filter(d => getEstadoFromId(d.estatus_id) === 'asignado').length;
    const mantenimiento = dispositivosArray.filter(d => getEstadoFromId(d.estatus_id) === 'mantenimiento').length;
    const dañados = dispositivosArray.filter(d => getEstadoFromId(d.estatus_id) === 'dañado').length;
    
    return { total, disponibles, asignados, mantenimiento, dañados };
  }, [dispositivos]);

  const handleDeleteClick = (id: number) => {
    setDispositivoToDelete(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (dispositivoToDelete) {
      deleteDispositivo(dispositivoToDelete);
      setShowConfirmModal(false);
      setDispositivoToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setDispositivoToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando dispositivos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Dispositivos</h1>
          <p className="mt-2 text-gray-600">Administra todos los dispositivos Samsung de tu organización</p>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Smartphone className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                    <dd className="text-lg font-medium text-gray-900">{estadisticas.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Disponibles</dt>
                    <dd className="text-lg font-medium text-gray-900">{estadisticas.disponibles}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Asignados</dt>
                    <dd className="text-lg font-medium text-gray-900">{estadisticas.asignados}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Mantenimiento</dt>
                    <dd className="text-lg font-medium text-gray-900">{estadisticas.mantenimiento}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Dañados</dt>
                    <dd className="text-lg font-medium text-gray-900">{estadisticas.dañados}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controles */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                {/* Búsqueda */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar dispositivos..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Filtro */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={filterEstado}
                    onChange={(e) => setFilterEstado(e.target.value)}
                  >
                    <option value="todos">Todos los estados</option>
                    <option value="disponible">Disponible</option>
                    <option value="asignado">Asignado</option>
                    <option value="mantenimiento">Mantenimiento</option>
                    <option value="dañado">Dañado</option>
                  </select>
                </div>
              </div>

              {/* Botón Agregar */}
              <button 
                onClick={() => navigate('/dispositivos/agregar')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Dispositivo
              </button>
            </div>
          </div>
        </div>

        {/* Lista de Dispositivos */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredDispositivos.length === 0 ? (
              <li className="px-6 py-8 text-center">
                <Smartphone className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay dispositivos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || filterEstado !== "todos" 
                    ? "No se encontraron dispositivos que coincidan con los filtros."
                    : "Comienza agregando un nuevo dispositivo."
                  }
                </p>
              </li>
            ) : (
              filteredDispositivos.map((dispositivo: DispositivoInterface) => (
                <li key={dispositivo.id}>
                  <div className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {getEstadoIcon(dispositivo.estatus_id)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {dispositivo.nombre_dispositivo || 'Sin nombre'}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getEstadoColor(getEstadoFromId(dispositivo.estatus_id))}`}>
                            {getEstadoFromId(dispositivo.estatus_id)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <span>Modelo: {dispositivo.modelo || 'N/A'}</span>
                          <span>•</span>
                          <span>Serie: {dispositivo.numero_serie || 'N/A'}</span>
                          <span>•</span>
                          <span>IMEI: {dispositivo.imei || 'N/A'}</span>
                          {dispositivo.responsable_actual && (
                            <>
                              <span>•</span>
                              <span>Asignado a: {dispositivo.responsable_actual}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          className="text-blue-600 hover:text-blue-800 p-1"
                          onClick={() => navigate(`/dispositivos/editar/${dispositivo.id}`)}
                          title="Editar dispositivo"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          className="text-red-600 hover:text-red-800 p-1"
                          onClick={() => handleDeleteClick(dispositivo.id)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Modal de Confirmación */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">Eliminar Dispositivo</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    ¿Estás seguro de que deseas eliminar este dispositivo? Esta acción no se puede deshacer.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <button
                    className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dispositivos;
